<template>
  <div class="mx-sm-1 mt-9">
    <div class="page-header">
      <h1 class="page-title fw-bold">Produits</h1>
    </div>

    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h3 class="card-title fw-bold mb-0">Liste des Produits</h3>
        <div class="card-options d-flex gap-2">
          <button class="btn btn-outline-success btn-sm" @click="showCreateProductModal">
            <i class="fe fe-plus-circle me-1"></i> Ajouter
          </button>
          <button class="btn btn-outline-info btn-sm" @click="exportCSV">
            <i class="fe fe-download me-1"></i> Exporter
          </button>

          <button class="btn btn-outline-primary btn-sm" @click="triggerFileInput">
            <i class="fe fe-upload me-1"></i> Importer
          </button>
          <input type="file" ref="fileInput" class="d-none" accept=".xlsx, .xls" @change="importCSV" />
        </div>
      </div>

      <div class="card-body">
        <TableWithFilters
            :columns="columns"
            :filterableFields="filterableFields"
            :row-actions="rowActions"
            :items="products"
            @edit="onEdit"
            @delete="onDelete"
            @history="onHistory"
        >

          <template #cell-image="{ item }">
            <img
                v-if="item.images?.length"
                :src="item.images[0].url"
                alt="image"
                style="width: 60px; height: 60px; object-fit: cover; border-radius: 4px;"
            />
            <span v-else class="text-muted">Aucune image</span>
          </template>

          <template #cell-name="{ item }">
            <strong>{{ item.name }}</strong>
            <br>
            <small class="text-muted">{{ item.code }}</small>
          </template>

          <template #cell-referencePrice="{ item }">
            {{ item.referencePrice.toFixed(2) }} €
          </template>

          <template #cell-managementType="{ item }">
  <span
      class="badge"
      :class="{
      'bg-primary': item.managementType === 'CUMP',
      'bg-success': item.managementType === 'FIFO',
      'bg-warning': item.managementType === 'LIFO'
    }"
  >
    {{
      item.managementType === 'CUMP'
          ? 'CUMP'
          : item.managementType === 'FIFO'
              ? 'FIFO'
              : item.managementType === 'LIFO'
                  ? 'LIFO'
                  : item.managementType
    }}
  </span>
          </template>

          <template #cell-unitCode="{ item }">
  <span class="badge bg-secondary">
    {{ item.baseUnitCode || item.unitDescription }}
  </span>
          </template>

          <template #cell-stockQuantity="{ item }">
  <span :class="item.stockQuantity <= 5 ? 'text-danger' : 'text-success'">
    {{ item.stockQuantity }}
  </span>
          </template>

          <template #cell-categoryCode="{ item }">
            {{ item.categoryDescription || item.categoryCode }}
          </template>

          <template #cell-familyCode="{ item }">
            {{ item.familyDescription || item.familyCode }}
          </template>
        </TableWithFilters>
      </div>
    </div>

    <!-- Modal de création -->
    <modal-create-product
        :allFamilies="allFamilies"
        :allUnits="allUnits"
        :allCategories="allCategories"
        :product="selectedProduct"
        :managementTypeOptions="managementTypeOptions"
        @refresh="loadProducts"
    />

    <modal-stock-movements
        :product="selectedProduct"
        :movements="selectedProductMovements"
    />

    <div class="modal fade"
         id="modal-import-preview"
         tabindex="-1"
         role="dialog"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog modal-xl" role="document">
        <div class="modal-content">
          <div class="modal-header bg-secondary text-white">
            <h5 class="modal-title">Prévisualisation de l'import produits</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"
                    @click="resetCsvPreview"></button>
          </div>

          <div class="modal-body" v-if="csvPreview">
            <!-- badges résumé -->
            <div class="mb-3 d-flex flex-wrap gap-2 align-items-center">
              <span class="badge bg-secondary">Total lignes : {{ csvPreview.totalRows ?? 0 }}</span>
              <span class="badge bg-success">À créer : {{ (csvPreview.toCreate || []).length }}</span>
              <span class="badge bg-info">Existants : {{ (csvPreview.existingDb || []).length }}</span>
              <span class="badge bg-warning text-dark">Doublons fichier : {{ (csvPreview.duplicatesFile || []).length }}</span>
              <span class="badge bg-danger">Invalides : {{ (csvPreview.invalid || []).length }}</span>
              <span class="badge bg-danger">Réfs manquantes : {{ (csvPreview.missingRefs || []).length }}</span>
            </div>

            <!-- TO CREATE -->
            <div v-if="csvPreview.toCreate && csvPreview.toCreate.length" class="mb-4">
              <h6 class="fw-bold mb-2">Lignes à créer</h6>
              <div class="table-responsive mb-2">
                <table class="table table-sm table-bordered text-nowrap">
                  <thead class="bg-light">
                  <tr>
                    <th>#</th>
                    <th>reference</th>
                    <th>name</th>
                    <th>price</th>
                    <th>initialStock</th>
                    <th>familyCode</th>
                    <th>categoryCode</th>
                    <th>subcategoryCode</th>
                    <th>baseUnitCode</th>
                    <th>managementType</th>
                    <th>images</th>
                    <th>Problems</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(r, i) in csvPreview.toCreate" :key="'create-'+i">
                    <td>{{ r._rowIndex || (i+2) }}</td>
                    <td>{{ r.reference }}</td>
                    <td>{{ r.name }}</td>
                    <td>{{ r.referencePrice }}</td>
                    <td>{{ r.initialStock }}</td>
                    <td>{{ r.familyCode }}</td>
                    <td>{{ r.categoryCode }}</td>
                    <td>{{ r.subcategoryCode }}</td>
                    <td>{{ r.baseUnitCode }}</td>
                    <td>{{ r.managementType }}</td>
                    <td>
                    <span v-if="r.images && r.images.length" class="small">
                      {{ r.images.slice(0,2).join(',') }}<span v-if="r.images.length>2">…</span>
                    </span>
                    </td>
                    <td class="text-danger small">
                      <!-- Show problems if any were attached to row (from missingRefs) -->
                      <div v-if="csvPreview.missingRefs && csvPreview.missingRefs.length">
                        <div v-for="m in csvPreview.missingRefs.filter(x => (x.row._rowIndex||x.rowIndex) === (r._rowIndex || (i+2)))" :key="m.row._rowIndex">
                          <strong v-for="p in m.problems" :key="p.field">{{ p.field }} </strong>
                        </div>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- ALREADY EXISTING -->
            <details v-if="csvPreview.existingDb && csvPreview.existingDb.length" class="mb-3">
              <summary class="fw-bold">Déjà existants (ignorés)</summary>
              <div class="table-responsive mt-2">
                <table class="table table-sm table-bordered text-nowrap mb-0">
                  <thead class="bg-light">
                  <tr><th>#</th><th>reference</th><th>name</th><th>family</th><th>category</th></tr>
                  </thead>
                  <tbody>
                  <tr v-for="(r, i) in csvPreview.existingDb" :key="'exist-'+i">
                    <td>{{ r._rowIndex || (i+2) }}</td>
                    <td>{{ r.reference }}</td>
                    <td>{{ r.name }}</td>
                    <td>{{ r.familyCode }}</td>
                    <td>{{ r.categoryCode }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </details>

            <!-- DUPLICATES -->
            <details v-if="csvPreview.duplicatesFile && csvPreview.duplicatesFile.length" class="mb-3">
              <summary class="fw-bold">Doublons dans le fichier</summary>
              <div class="table-responsive mt-2">
                <table class="table table-sm table-bordered text-nowrap mb-0">
                  <thead class="bg-light">
                  <tr><th>#</th><th>reference</th><th>name</th><th>note</th></tr>
                  </thead>
                  <tbody>
                  <tr v-for="(r, i) in csvPreview.duplicatesFile" :key="'dup-'+i">
                    <td>{{ r._rowIndex || (i+2) }}</td>
                    <td>{{ r.reference }}</td>
                    <td>{{ r.name }}</td>
                    <td class="small text-muted">Doublon (même reference/name)</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </details>

            <!-- INVALID ROWS (missing reference/name) -->
            <details v-if="csvPreview.invalid && csvPreview.invalid.length" class="mb-3">
              <summary class="fw-bold">Invalides (champ obligatoire manquant)</summary>
              <div class="table-responsive mt-2">
                <table class="table table-sm table-bordered text-nowrap mb-0">
                  <thead class="bg-light">
                  <tr><th>#</th><th>reference</th><th>name</th><th>note</th></tr>
                  </thead>
                  <tbody>
                  <tr v-for="(r, i) in csvPreview.invalid" :key="'inv-'+i">
                    <td>{{ r._rowIndex || (i+2) }}</td>
                    <td>{{ r.reference }}</td>
                    <td>{{ r.name }}</td>
                    <td class="small text-danger">Manque reference et/ou name</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </details>

            <!-- MISSING REFERENCES (family/category/unit/managementType invalid) -->
            <!-- EXISTING DB (avec message explicite) -->
            <details v-if="csvPreview.existingDb && csvPreview.existingDb.length" class="mb-3">
              <summary class="fw-bold">Déjà existants (ignorés)</summary>
              <div class="table-responsive mt-2">
                <table class="table table-sm table-bordered text-nowrap mb-0">
                  <thead class="bg-light">
                  <tr><th>#</th><th>reference</th><th>name</th><th>Messages</th></tr>
                  </thead>
                  <tbody>
                  <tr v-for="(r, i) in csvPreview.existingDb" :key="'exist-'+i">
                    <td>{{ r._rowIndex || (i+2) }}</td>
                    <td>{{ r.reference }}</td>
                    <td>{{ r.name }}</td>
                    <td class="small text-danger">
                      <ul class="mb-0 ps-3">
                        <li v-for="(p, j) in (r.problems || [])" :key="j">{{ p.message || (p.field + ': ' + p.value) }}</li>
                      </ul>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </details>

            <!-- MISSING REFS -->
            <details v-if="csvPreview.missingRefs && csvPreview.missingRefs.length" class="mb-3">
              <summary class="fw-bold">Problèmes de références (famille / catégorie / sous-catégorie / unité / gestion)</summary>
              <div class="table-responsive mt-2">
                <table class="table table-sm table-bordered text-nowrap mb-0">
                  <thead class="bg-light">
                  <tr><th>#</th><th>reference</th><th>name</th><th>Problèmes détectés</th></tr>
                  </thead>
                  <tbody>
                  <tr v-for="(m, i) in csvPreview.missingRefs" :key="'miss-'+i">
                    <td>{{ (m.row && (m.row._rowIndex || m.rowIndex)) || (i+2) }}</td>
                    <td>{{ m.row?.reference }}</td>
                    <td>{{ m.row?.name }}</td>
                    <td class="small text-danger">
                      <ul class="mb-0 ps-3">
                        <li v-for="(p, j) in m.problems" :key="j">{{ p.message || (p.field + ': ' + p.value) }}</li>
                      </ul>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </details>

          </div>

          <div class="modal-body" v-else>
            <p class="text-muted">Aucune prévisualisation disponible.</p>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-gray" data-bs-dismiss="modal" @click="resetCsvPreview">
              Fermer
            </button>

            <button type="button"
                    class="btn btn-success"
                    :disabled="!(csvPreview && csvPreview.toCreate && csvPreview.toCreate.length) || savingImport"
                    @click="saveImportedProducts">
              <i class="fe fe-check me-1"></i>
              Enregistrer ({{ (csvPreview && csvPreview.toCreate) ? csvPreview.toCreate.length : 0 }})
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import ModalCreateProduct from "../../../components/modals/ModalCreateProduct.vue";
  import TableWithFilters from "../../../components/TableWithFilters.vue";
  import {FILTER_TYPES, productColumns, productFilterableFields, productRowActions} from "../../../helpers/constants";
  import {_chunkArray, confirmAction, showValidationErrors} from "../../../helpers/helpers";
  import ModalStockMovements from "../../../components/modals/ModalStockMovements.vue";
  import {API} from "../../../helpers/api-endpoints";
  import * as XLSX from "xlsx";

  export default {
    name: "Produits",
    components: {
      ModalStockMovements,
      ModalCreateProduct,
      TableWithFilters,
    },
    data() {
      return {
        products: [],
        selectedProduct: null,
        selectedProductMovements: [],
        columns: productColumns ,
        filterableFields: productFilterableFields ,
        rowActions: productRowActions,
        allFamilies: [],
        allCategories: [],
        allUnits: [],
        managementTypeOptions: [
          { value: 'FIFO', label: 'Premier entré, premier sorti (FIFO)' },
          { value: 'LIFO', label: 'Dernier entré, premier sorti (LIFO)' },
          { value: 'CUMP', label: 'Coût unitaire moyen pondéré (CUMP)' },
        ],
        csvPreview: {
          total: 0,
          toCreate: [],
          existingDb: [],
          duplicatesFile: [],
          invalid: [],
          headers: [],
        },
        savingImport: false,
      };
    },
    methods: {
      async saveImportedProducts() {
        if (!this.csvPreview || !(this.csvPreview.toCreate && this.csvPreview.toCreate.length)) {
          this.$toast.info("Aucune ligne à créer.");
          return;
        }

        // confirm if there are problems
        const missing = (this.csvPreview.missingRefs || []).length;
        if (missing) {
          if (!confirm(`${missing} lignes ont des références manquantes/invalides. Continuer l'enregistrement des lignes valides ?`)) {
            return;
          }
        }

        const payloadList = this.csvPreview.toCreate.map(r => ({
          name: r.name,
          description: r.description,
          referencePrice: Number(r.referencePrice || 0),
          initialStock: Number(r.initialStock || 0),
          images: r.images || [],
          sellingUnits: r.sellingUnits || [],
          managementType: r.managementType,
          commissionRate: Number(r.commissionRate || 0),
          familyCode: r.familyCode,
          categoryCode: r.categoryCode,
          subcategoryCode: r.subcategoryCode,
          baseUnitCode: r.baseUnitCode,
          quantityAlert: Number(r.quantityAlert || 0),
          garantie: r.garantie || '',
          fabricant: r.fabricant || '',
          expiryDate: r.expiryDate || null,
          reference: r.reference || null,
        }));

        const BATCH_SIZE = 50; // ajuste si besoin
        const batches = _chunkArray(payloadList, BATCH_SIZE);

        const { confirmed } = await confirmAction("validate");
        if (!confirmed) return;

        this.savingImport = true;
        this.$loading(true);

        let createdCount = 0;
        const failedRows = [];

        try {
          for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];
            await this.$http.post(API.PRODUCT_END_POINT,{ products: batch });
            createdCount += batch.length;
          }

          const total = payloadList.length;
          this.$toast.success(`${createdCount}/${total} produits créés avec succès.`);


          if (failedRows.length) {
            // map failed rows to CSV and offer download
            this.$toast.warning(`${failedRows.length} lignes ont échoué. Fichier téléchargé (failedRows.csv).`);
          }


          $('#modal-import-preview').modal('hide');
          this.resetCsvPreview();
          await this.loadProducts();
        } catch (err) {
          console.error(err);
          if (err.response && err.response.data && err.response.data.errors) {
            const errors = err.response.data.errors;
            Object.values(errors).forEach(msg => {
              this.$toast.error(msg);
            });
          } else {
            this.$toast.error("Erreur lors de l'importation des produits.");
          }
        } finally {
          this.$loading(false);
          this.savingImport = false;
        }
      },
      // <<< Reset preview
      resetCsvPreview() {
        this.csvPreview = {
          total: 0, toCreate: [], existingDb: [], duplicatesFile: [], invalid: [], headers: [],
        };
      },
      buildCsvPreviewProducts(arrayBuffer) {
        try {
          const data = new Uint8Array(arrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });

          // find sheet named Products (tolerant case)
          const targetSheetName = workbook.SheetNames.find(n => n && n.toString().toLowerCase() === "products") ||
              workbook.SheetNames.find(n => n && n.toString().toLowerCase() === "produits")

          if (!targetSheetName) {
            this.$toast.error("Feuille introuvable (attendu 'Products' ou 'Produits').");
            this.resetCsvPreview();
            return;
          }

          const worksheet = workbook.Sheets[targetSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (!jsonData || !jsonData.length) {
            this.$toast.warning("Fichier XLSX vide.");
            this.resetCsvPreview();
            return;
          }

          const headersRaw = jsonData[0];
          const headers = headersRaw.map(h => (h ?? "").toString().trim());
          const findIndexIgnoreCase = (arr, key) => arr.findIndex(h => h.toString().toLowerCase() === key.toLowerCase());

          const nameIdx = findIndexIgnoreCase(headers, "name");
          const priceIdx = findIndexIgnoreCase(headers, "referencePrice");
          const initialStockIdx = findIndexIgnoreCase(headers, "initialStock");
          const familyCodeIdx = findIndexIgnoreCase(headers, "familyCode");
          const categoryCodeIdx = findIndexIgnoreCase(headers, "categoryCode");
          const subcategoryCodeIdx = findIndexIgnoreCase(headers, "subcategoryCode");
          const baseUnitCodeIdx = findIndexIgnoreCase(headers, "baseUnitCode");
          const managementTypeIdx = findIndexIgnoreCase(headers, "managementType");
          const quantityAlertIdx = findIndexIgnoreCase(headers, "quantityAlert");
          const descriptionIdx = findIndexIgnoreCase(headers, "description");
          const commissionRateIdx = findIndexIgnoreCase(headers, "commissionRate");

          // --- validation : toutes les colonnes requises doivent exister
          const requiredCols = [
            { key: "name", idx: nameIdx },
            { key: "referencePrice", idx: priceIdx },
            { key: "initialStock", idx: initialStockIdx },
            { key: "familyCode", idx: familyCodeIdx },
            { key: "categoryCode", idx: categoryCodeIdx },
            // { key: "subcategoryCode", idx: subcategoryCodeIdx },
            { key: "baseUnitCode", idx: baseUnitCodeIdx },
            { key: "managementType", idx: managementTypeIdx },
            { key: "quantityAlert", idx: quantityAlertIdx },
            // { key: "description", idx: descriptionIdx }
            // ajoute 'reference' si tu veux l'exiger aussi
          ];

          const missingHeaders = requiredCols.filter(c => c.idx === -1).map(c => c.key);
          if (missingHeaders.length) {
            this.$toast.error(`Colonnes manquantes dans le fichier : ${missingHeaders.join(", ")}`);
            this.resetCsvPreview();
            return;
          }

          // require at least reference (or name) + family/category/unit if you want:
          if (nameIdx === -1) {
            this.$toast.error("Le XLSX doit contenir au moins la colonne 'name'.");
            this.resetCsvPreview();
            return;
          }

          // normalize existing lists (defensive)
          const familiesSet = new Set((this.allFamilies || []).map(f => (f.code || "").toString().toLowerCase()));
          const categoriesMap = new Map();      // Catégories de profondeur 0
          const subcategoriesMap = new Map();   // Sous-catégories avec parent valide

          (this.allCategories || []).forEach(c => {
            if (!c.parentCode) {
              // profondeur 0 = catégorie
              categoriesMap.set(c.code.toLowerCase(), c);
            } else {
              // profondeur 1 = sous-catégorie
              if (categoriesMap.has(c.parentCode.toLowerCase())) {
                subcategoriesMap.set(c.code.toLowerCase(), c);
              } else {
                console.warn(`Sous-catégorie ignorée car parentCode introuvable: ${c.code}`);
              }
            }
          });
          const unitsSet = new Set((this.allUnits || []).map(u => (u.code || "").toString().toLowerCase()));
          // managementTypes can be an array of strings or objects { code }
          const managementTypesSet = new Set((this.managementTypeOptions || []).map(m => (m.value ?? m).toString().toLowerCase()));

          // We'll dedupe on reference if present else name
          const seen = new Set();
          const duplicatesFile = [];
          const rows = [];
          for (let i = 1; i < jsonData.length; i++) {
            const line = jsonData[i] || [];
            // const reference = referenceIdx !== -1 ? (line[referenceIdx] ?? "").toString().trim() : "";
            const name = nameIdx !== -1 ? (line[nameIdx] ?? "").toString().trim() : "";
            const rowKey = (name).toLowerCase();

            const row = {
              name,
              description: (descriptionIdx !== -1 ? (line[descriptionIdx] ?? "").toString().trim() : ""),
              referencePrice: priceIdx !== -1 ? Number(line[priceIdx] ?? 0) : 0,
              initialStock: initialStockIdx !== -1 ? Number(line[initialStockIdx] ?? 0) : 0,
              // images: imagesIdx !== -1 ? ((line[imagesIdx] ?? "").toString().split(",").map(s => s.trim()).filter(Boolean)) : [],
              sellingUnits: [], // you may parse more columns into this if you want
              managementType: (managementTypeIdx !== -1 ? (line[managementTypeIdx] ?? "").toString().trim() : ""),
              commissionRate: 0,
              familyCode: (familyCodeIdx !== -1 ? (line[familyCodeIdx] ?? "").toString().trim() : ""),
              categoryCode: (categoryCodeIdx !== -1 ? (line[categoryCodeIdx] ?? "").toString().trim() : ""),
              subcategoryCode: (subcategoryCodeIdx !== -1 ? (line[subcategoryCodeIdx] ?? "").toString().trim() : ""),
              baseUnitCode: (baseUnitCodeIdx !== -1 ? (line[baseUnitCodeIdx] ?? "").toString().trim() : ""),
              quantityAlert: quantityAlertIdx !== -1 ? Number(line[quantityAlertIdx] ?? 0) : 0,
              garantie: "",
              fabricant: "",
              expiryDate: null,

              // helper for validation
              _rowIndex: i + 1 // spreadsheet row number (1-based header + 1)
            };

            if (!rowKey) {
              // push anyway, will be marked invalid later
              rows.push(row);
              continue;
            }

            if (seen.has(rowKey)) {
              duplicatesFile.push(row);
            } else {
              seen.add(rowKey);
              rows.push(row);
            }
          }

          // basic validity: require reference or name and family/category/unit if you want
          const cleaned = rows.filter(r => (r.name) ); // keep rows with some identifier
          const invalid = cleaned.filter(r => !r.name);

          // check missing references (family/category/subcategory/unit/managementType)
          const missingRefs = [];
          const uniqueByFile = []; // rows that pass basic duplication/format checks

          // existing in DB: match by reference (if you use reference) or name
          const existingRefsSet = new Set(
              (this.products || []).map(p => ((p.name) || "").toString().toLowerCase())
          );



          for (const r of cleaned) {
            // skip rows that were earlier detected as duplicates in file (they are in duplicatesFile)
            // but still validate them if you want; here on unique rows only:
            if (!r || (!r.reference && !r.name && !r.code && !r.label)) {
              continue;
            }

            const problems = [];

            // family
            if (r.familyCode) {
              const f = r.familyCode.toString().trim();
              if (!familiesSet.has(f.toLowerCase())) {
                problems.push({
                  field: "familyCode",
                  value: f,
                  message: `Family code '${f}' not found`
                });
              }
            } else {
              problems.push({
                field: "familyCode",
                value: r.familyCode,
                message: `Family code is empty`
              });
            }

            if (r.categoryCode) {
              if (!categoriesMap.has(r.categoryCode.toLowerCase())) {
                problems.push({ field: "categoryCode", value: r.categoryCode });
              }
            }

            if (r.subcategoryCode) {
              const sc = subcategoriesMap.get(r.subcategoryCode.toLowerCase());
              if (!sc) {
                problems.push({ field: "subcategoryCode", value: r.subcategoryCode });
              } else if (r.categoryCode.toLowerCase() !== sc.parentCode.toLowerCase()) {
                problems.push({ field: "subcategoryCode", value: `${r.subcategoryCode} (parent attendu: ${sc.parentCode})` });
              }
            }

            // base unit
            if (r.baseUnitCode) {
              const u = r.baseUnitCode.toString().trim();
              if (!unitsSet.has(u.toLowerCase())) {
                problems.push({
                  field: "baseUnitCode",
                  value: u,
                  message: `Base unit code '${u}' not found`
                });
              }
            } else {
              problems.push({
                field: "baseUnitCode",
                value: r.baseUnitCode,
                message: `Base unit code is empty`
              });
            }

            // management type
            if (r.managementType) {
              const m = r.managementType.toString().trim();
              if (!managementTypesSet.has(m.toLowerCase())) {
                problems.push({
                  field: "managementType",
                  value: m,
                  message: `Management type '${m}' not found`
                });
              }
            } else {
              problems.push({
                field: "managementType",
                value: r.managementType,
                message: `Management type is empty`
              });
            }

            // check product reference existence in DB (explicit)
            const refKey = ((r.name) || "").toString().toLowerCase();
            if (refKey && existingRefsSet.has(refKey)) {
              problems.push({
                field: "name",
                value: r.name,
                message: `Product reference '${r.name}' already exists in database`
              });
              // NOTE: we don't immediately exclude it here; it will be put into existingDb later
            }

            if (problems.length) {
              missingRefs.push({ row: r, problems });
            } else {
              uniqueByFile.push(r);
            }
          }

// maintenant on calcule existingDb / toCreate (comme avant) — on ajoute aussi un message explicite aux existants
          const existingDb = uniqueByFile
              .filter(r => existingRefsSet.has(((r.reference || r.name) || "").toLowerCase()))
              .map(r => ({
                ...r,
                problems: [{ field: "reference", message: `Product reference '${r.reference || r.name}' already exists in database` }]
              }));

          const toCreate = uniqueByFile.filter(r => !existingRefsSet.has(((r.reference || r.name) || "").toLowerCase()));



          this.csvPreview = {
            totalRows: jsonData.length - 1,
            toCreate,
            existingDb,
            duplicatesFile,
            invalid,
            missingRefs,
            headers,
          };

          // show modal or UI summary
          $('#modal-import-preview').modal('show');

        } catch (err) {
          console.error("Erreur buildCsvPreviewProducts :", err);
          this.$toast.error("Erreur lors du traitement du fichier produits.");
          this.resetCsvPreview();
        }
      },
      async importCSV(event) {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            // pass the ArrayBuffer to the parser
            this.buildCsvPreviewProducts(e.target.result);
          } catch (err) {
            console.error("Erreur import CSV produits :", err);
            this.$toast.error("Erreur lors de la lecture du CSV produits.");
          } finally {
            event.target.value = ""; // reset input
          }
        };
        reader.readAsArrayBuffer(file);
      },
      triggerFileInput() { this.$refs.fileInput.click(); },
      showCreateProductModal() {
        this.selectedProduct = null;
        $('#modal-create-product').modal('show');
      },
      loadFamilies() {
        this.$http.get(API.REGISTRY_FAMILIES).then(({ data }) => this.allFamilies = data);
      },
      loadCategories() {
        this.$http.get(API.REGISTRY_CATEGORIES).then(({ data }) => this.allCategories = data);
      },
      loadUnits() {
        this.$http.get(API.REGISTRY_UNITS).then(({ data }) => this.allUnits = data);
      },
      loadProducts() {
        this.$loading(true);
        this.$http
            .get(API.PRODUCT_END_POINT)
            .then(({ data }) => {
              this.products = data;
            })
            .catch((err) => {
              console.error("Erreur lors du chargement des produits :", err);
            })
            .finally(() => this.$loading(false));
      },
      onEdit(item) {
        this.selectedProduct = {
          ...item,
          categoryCode: item.categoryCode
              ? { code: item.categoryCode, name: item.categoryName || item.categoryCode }
              : null,
          familyCode: item.familyCode
              ? { code: item.familyCode, name: item.familyName || item.familyCode }
              : null,
          baseUnitCode: item.baseUnitCode
              ? { code: item.baseUnitCode, name: item.baseUnitName || item.baseUnitCode }
              : null
        };

        $('#modal-create-product').modal('show');
      },
      onHistory(item) {
        this.selectedProduct = { ...item };
        this.$loading(true);
        this.$http
            .get(API.PRODUCT_HISTORY(item.id))
            .then(({ data }) => {
              this.selectedProductMovements = data;
              $('#modal-stock-movements').modal('show');
            })
            .catch((err) => {
              console.error("Erreur lors du chargement des mouvements :", err);
              swal("Erreur", "Impossible de charger les mouvements de stock.", "error");
            })
            .finally(() => this.$loading(false));
      },
      async onDelete(item) {
        try {
          const { confirmed } = await confirmAction("delete", {
            title: "Confirmation",
            text: `Voulez-vous vraiment supprimer le produit "${item.name}" ?`,
          });

          if (!confirmed) return;

          this.$loading(true);
          await this.$http.delete(API.PRODUCT_DELETE(item.id));
          this.$toast.success("Produit supprimé avec succès !");
          await this.loadProducts();
        } catch (err) {
          console.error("Erreur lors de la suppression :", err);
          this.$toast.error("Impossible de supprimer le produit.");
        } finally {
          this.$loading(false);
        }
      },
      exportCSV() {
        const headers = this.columns.map(col => col.label);
        const rows = this.products.map(row =>
            this.columns.map(col => `"${row[col.key] ?? ''}"`).join(",")
        );
        const csvContent = [headers.join(","), ...rows].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "produits.csv");
        link.click();
        URL.revokeObjectURL(url);
      },
    },
    mounted() {
      this.loadProducts();
    },
    created() {
      this.loadFamilies();
      this.loadCategories();
      this.loadUnits();
      // this.loadProducts();
    },
  };
</script>
